import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import SouthEastRoundedIcon from "@mui/icons-material/SouthEastRounded";
import AddRoundedIcon from "@mui/icons-material/Add";

import Button, { ButtonTypes } from "../../../../../components/Button";
import { MultipleLineChartComponent } from "../../../../../components/Charts";
import { useResponsive } from "../../../../../hooks/useResponsive";
import {
  formatSignedCurrency,
  formatSignedPercent,
  getNumberFormat,
  toTone,
} from "../../../../../utils";
import { BREAKPOINTS } from "../../../../../utils/constants";

import { HERO_CHART_HEIGHT } from "../../constants";
import { PeriodFigure } from "../PeriodFigure";
import { RetryNote } from "../RetryNote";
import { SkeletonBox } from "../styles";
import {
  ChartArea,
  ChartEmpty,
  CurrentValue,
  Footnote,
  GrowthChip,
  GrowthPercent,
  GrowthValue,
  Headline,
  HeroCard,
  PortfolioLabel,
  SubStat,
  SubStatLabel,
  SubStatValue,
  SubStats,
  TopBar,
} from "./styles";
import { SavingsHeroProps } from "./types";

export const SavingsHero: React.FC<SavingsHeroProps> = ({
  periodLabel,
  currentValue,
  depositedValue,
  withdrawnValue,
  cashbackValue,
  netContributed,
  changeValue,
  changePercent,
  chartSeries,
  chartXLabels,
  totalsStatus,
  chartStatus,
  onRetryTotals,
  onRetryChart,
  onCreateSavings,
}) => {
  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);

  const tone = toTone(changeValue);
  const headerLabel = `Savings · ${periodLabel}`;
  // Suppress the gain chip when there is no contributed base to measure against
  // (changePercent is null): a percentage would be meaningless.
  const showGrowth = changePercent !== null;
  // A trend needs at least two points; a single dot reads as a broken chart.
  const hasTrend = chartXLabels.length >= 2;
  const chartHeight = isCompact
    ? HERO_CHART_HEIGHT.compact
    : HERO_CHART_HEIGHT.regular;

  // Arrow direction tracks the tone so a loss never points up.
  let TrendIcon: typeof ArrowOutwardRoundedIcon | null = null;
  if (tone === "positive") {
    TrendIcon = ArrowOutwardRoundedIcon;
  } else if (tone === "negative") {
    TrendIcon = SouthEastRoundedIcon;
  }

  const createButton = isCompact ? null : (
    <Button
      type={ButtonTypes.primary}
      size="medium"
      onClick={onCreateSavings}
      aria-label="Create savings"
    >
      <AddRoundedIcon fontSize="small" />
      Create savings
    </Button>
  );

  const growthChip = showGrowth ? (
    <GrowthChip $tone={tone}>
      {TrendIcon ? <TrendIcon /> : null}
      <GrowthValue>{formatSignedCurrency(changeValue)}</GrowthValue>
      <GrowthPercent>{formatSignedPercent(changePercent)}</GrowthPercent>
    </GrowthChip>
  ) : null;

  let chartContent: React.ReactNode;
  if (chartStatus === "loading") {
    chartContent = <ChartEmpty aria-busy="true">Loading chart…</ChartEmpty>;
  } else if (chartStatus === "unavailable") {
    chartContent = (
      <ChartEmpty>
        <span>
          <RetryNote
            message="Couldn't load the chart for this period."
            onRetry={onRetryChart}
          />
        </span>
      </ChartEmpty>
    );
  } else if (hasTrend) {
    chartContent = (
      <MultipleLineChartComponent
        series={chartSeries}
        xLabels={chartXLabels}
        height={chartHeight}
        hideYAxis
        hideXAxisLine
        hideLegend={isCompact}
      />
    );
  } else {
    chartContent = (
      <ChartEmpty>Not enough history to chart this period yet.</ChartEmpty>
    );
  }

  const isTotalsLoading = totalsStatus === "loading";

  let headline: React.ReactNode = (
    <>
      <CurrentValue>{getNumberFormat(currentValue)} €</CurrentValue>
      {growthChip}
    </>
  );
  const hasCashback = cashbackValue > 0;
  const footnoteGrowth = hasCashback ? "interest and cashback" : "interest";
  let footnote: React.ReactNode = `${getNumberFormat(netContributed)} € net contributed, before ${footnoteGrowth}`;
  if (isTotalsLoading) {
    headline = <SkeletonBox width="240px" height="48px" />;
    footnote = "Loading this period's totals…";
  } else if (totalsStatus === "unavailable") {
    headline = <CurrentValue>—</CurrentValue>;
    footnote = (
      <RetryNote
        message="Couldn't load this period's totals."
        onRetry={onRetryTotals}
      />
    );
  }

  return (
    <HeroCard aria-busy={isTotalsLoading}>
      <TopBar>
        <PortfolioLabel>{headerLabel}</PortfolioLabel>
        {createButton}
      </TopBar>

      <Headline>{headline}</Headline>

      <ChartArea>{chartContent}</ChartArea>

      <SubStats>
        <SubStat>
          <SubStatLabel>Deposited</SubStatLabel>
          <SubStatValue>
            <PeriodFigure status={totalsStatus} skeletonWidth="90px">
              {getNumberFormat(depositedValue)} €
            </PeriodFigure>
          </SubStatValue>
        </SubStat>
        <SubStat>
          <SubStatLabel>Withdrawn</SubStatLabel>
          <SubStatValue>
            <PeriodFigure status={totalsStatus} skeletonWidth="90px">
              {getNumberFormat(withdrawnValue)} €
            </PeriodFigure>
          </SubStatValue>
        </SubStat>
        {hasCashback && (
          <SubStat>
            <SubStatLabel>Cashback</SubStatLabel>
            <SubStatValue>
              <PeriodFigure status={totalsStatus} skeletonWidth="90px">
                {getNumberFormat(cashbackValue)} €
              </PeriodFigure>
            </SubStatValue>
          </SubStat>
        )}
      </SubStats>

      <Footnote>{footnote}</Footnote>
    </HeroCard>
  );
};
